import { Box, Chip, CircularProgress, Divider, Typography } from "@mui/material";
import UserInteractionsService from "../../services/interactions/Interactions";
import { getSentimentMeta, TimelineItem, validateDate } from "../../utils/utils";
import InteractionIcon from "../../assets/accounts/interactioncolor.svg";
import PositiveIcon from "../../assets/accounts/positivePlus.svg";
import NeutralIcon from "../../assets/accounts/neutralPlus.svg";
import NegativeIcon from "../../assets/accounts/negativeMinus.svg";
import SelectedAsk7GIcon from "../../assets/common/ask7g-selected.svg";
import { useNavigate } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";


const GroupInteractionItem = ({
  interaction,
  isLast,
  navigationState,
  groupDateKey,
  anchorInteractionId,
  allInteractions,
}: {
  interaction: TimelineItem;
  isLast: boolean;
  navigationState?: Record<string, any>;
  groupDateKey?: string;
  anchorInteractionId?: string;
  allInteractions?: TimelineItem[];
}) => {
  const navigate = useNavigate();
  const resolvedParentPageIdentifier =
    navigationState?.parent_page_identifier ||
    (navigationState?.slideOverParent === "client-interaction-overview"
      ? "client-interaction-overview"
      : "account-customer");
  const insightsQuery =
    UserInteractionsService.useGetConversationInsights(
      { interactionId: interaction.id },
      !!interaction.id
    );

  const callQualityQuery =
    UserInteractionsService.useGetAbstractiveInsights(
      { interactionId: interaction.id },
      !!interaction.id
    );

  const sentiment = insightsQuery.data?.data?.[0]?.sentiment;
  const payload = callQualityQuery.data?.data?.payload;
  const roleKey = payload && Object.keys(payload)[0];
  const callQuality = roleKey ? payload[roleKey] : null;

  const getSentimentIcon = (sentiment?: string) => {
    if (sentiment === "Positive") {
      return (
        <img
          src={PositiveIcon}
          alt="Positive"
          width={12}
          height={12}
        />
      );
    }

    if (sentiment === "Negative") {
      return (
        <img
          src={NegativeIcon}
          alt="Negative"
          width={12}
          height={12}
        />
      );
    }

    if (sentiment === "Neutral") {
      return (
        <img
          src={NeutralIcon}
          alt="Neutral"
          width={12}
          height={12}
        />
      );
    }
  };

  // Helper to normalize keys
  const normalizeKey = (key: string) => {
    if (key.includes("opening")) return "opening";
    if (key.includes("execution")) return "execution";
    if (key.includes("closing")) return "closing";
    return key;
  };

  // Build metrics dynamically
  const callMetrics = ["opening", "execution", "closing"]
    .map((key) => {
      // find the actual key in callQuality that matches
      const dataKey = Object.keys(callQuality || {}).find(k => normalizeKey(k) === key);
      const score = dataKey ? callQuality[dataKey]?.score : undefined;
      const sentiment = getSentimentMeta(score);

      return {
        key,
        label: `Call ${key.charAt(0).toUpperCase() + key.slice(1)}`,
        sentiment,
      };
    })
    .filter(item => item.sentiment);

  const handleInteractionClick = () => {
    navigate(`/meeting/${interaction.id}`, {
      state: {
        parent_page_identifier: resolvedParentPageIdentifier,
        restoreTooltip: true,
        restoreTooltipInSlideOver: Boolean(navigationState?.slideOverParent),
        interactionId: interaction.id,
        tooltipGroupDateKey: groupDateKey,
        tooltipAnchorInteractionId: anchorInteractionId,
        tooltipPayload: {
          mode: "group",
          interactions: allInteractions && allInteractions.length > 0 ? allInteractions : [interaction],
          groupDateKey,
          anchorInteractionId,
        },
        ...navigationState,
      },
    });
  };

  return (
    <Box className="interaction-tooltip__group-item">
      {/* Header */}
      <Box className="interaction-tooltip__group-header">
        <Box className="interaction-tooltip__group-title-row clickable">
          <img src={InteractionIcon} width={16} className="interaction-icon"/>
          <Typography variant="h5" className="interaction-tooltip_title" onClick={handleInteractionClick}>
            {interaction.title}
          </Typography>
        </Box>
        <Box className="tooltip__time">
          <AccessTimeIcon sx={{ fontSize: "14px" }} />
          {validateDate(interaction.start_time, "hh:mm A")}
        </Box>
      </Box>

      {/* Content */}
      {insightsQuery.isLoading || callQualityQuery.isLoading ? (
        <CircularProgress size={14} />
      ) : (
        <>
        <Box className="interaction-tooltip__insight">
          <Box className="interaction-tooltip__insight-title">
            <img src={SelectedAsk7GIcon} className="interaction-tooltip__insight-icon" />
            <Typography variant="h1" fontWeight={600} color="#B902CA">
              Insight
            </Typography>

            {sentiment?.overallSentiment && (
              <Chip
                icon={getSentimentIcon(
                  sentiment.overallSentiment
                )}
                label={sentiment.overallSentiment}
                size="small"
                sx={{
                  height: 20,
                  border: "1px solid #E0E0E0",
                  borderRadius: "7px",
                  backgroundColor: "#FBFBFB",
                  color:
                    sentiment?.overallSentiment === "Positive"
                      ? "#158F4C"
                      : sentiment?.overallSentiment === "Negative"
                      ? "#B42318"
                      : sentiment?.overallSentiment === "Neutral"
                      ? "#DF9621"
                      :"",
                  fontWeight: 600,
                  fontSize: "11px"
                }}
              />
            )}
          </Box>
          <Box className="interaction-tooltip__insight-text">
            <Typography className="tooltip-insight">
              {sentiment?.sentimentSummary ||
                "No Recording Available."}
            </Typography>
          </Box>
        </Box>

        <Box className="interaction-tooltip__metrics">
          {callMetrics.map((item) => (
            <Box key={item.key} className="interaction-tooltip__metric-row">
              <img
                src={SelectedAsk7GIcon}
                className="interaction-tooltip__metric-icon"
              />
              <Typography variant="h5" color="#B902CA">
                {item.label}:{" "}
                <span
                  className={`sentiment-${item.sentiment!.label.toLowerCase()}`}
                >
                  {item.sentiment!.label}
                </span>
              </Typography>
            </Box>
          ))}
        </Box>
        {!isLast && (
          <Divider
            sx={{
              margin: "4px -22px 6px",
              borderColor: "#e3e3e3",
            }}
          />
        )}
        </>
      )}
    </Box>
  );
};

export default GroupInteractionItem;
